import { Link } from "react-router-dom";
import { Play, ArrowRight } from "lucide-react";
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useSite } from "@/contexts/SiteContext";
import { fileUrl } from "@/lib/api";
import { getYouTubeEmbedUrl, getYouTubeThumbnailUrl } from "@/lib/youtube";

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add("visible"); obs.unobserve(e.target); } }),
      { threshold: 0.08 }
    );
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

export default function Films() {
  useReveal();
  const [playing, setPlaying] = useState(null);
  const { films } = useSite() || {};
  const list = films || [];
  const playingEmbedUrl = getYouTubeEmbedUrl(playing?.url);

  return (
    <div data-testid="films-page" className="bg-[#070707] text-[#F5EFE4] min-h-screen">

      {/* Hero */}
      <section className="pt-40 md:pt-52 pb-16 md:pb-24 px-6 md:px-12" data-testid="films-hero" data-gsap="hero">
        <div className="max-w-[1380px] mx-auto">
          <span className="eyebrow text-[#B8914A] text-[0.6rem]" data-gsap="hero-kicker">Cinema</span>
          <h1 className="page-hero-title mt-3" data-gsap="hero-title">Films.</h1>
          <span className="gold-line mt-7 block" />
          <p className="font-display-italic mt-7 text-[rgba(245,239,228,0.75)]"
            data-gsap="hero-subtitle"
            style={{ fontSize: "clamp(1.3rem,2.5vw,1.9rem)", maxWidth: "680px", lineHeight: 1.5 }}>
            Crafted with emotion, rhythm, and storytelling. From vows and rituals to laughter and tears — personal, cinematic, timeless.
          </p>
        </div>
      </section>

      {/* Films Grid */}
      <section className="pb-28 px-6 md:px-12" data-gsap="reveal">
        {list.length === 0 ? (
          <div className="max-w-[1380px] mx-auto">
            <div className="border border-[rgba(184,145,74,0.2)] aspect-[21/9] md:aspect-[21/7] bg-gradient-to-br from-[#161616] to-[#0b0b0b] flex flex-col items-center justify-center text-center px-6" data-testid="films-empty">
              <div className="film-play-ring" style={{ width: "5rem", height: "5rem" }}>
                <Play size={22} fill="currentColor" className="ml-[3px] text-[#B8914A]" />
              </div>
              <div className="font-display mt-6" style={{ fontSize: "clamp(1.6rem,3vw,2.4rem)" }}>Films coming soon</div>
              <p className="eyebrow text-[0.58rem] text-[rgba(184,145,74,0.75)] mt-3">New cinematic stories are being edited</p>
            </div>
          </div>
        ) : (
        <div className="max-w-[1380px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10" data-gsap="stagger">
          {list.map((f, i) => {
            const thumbnailUrl = f.thumbnail ? fileUrl(f.thumbnail) : getYouTubeThumbnailUrl(f.url, "hqdefault");

            return (
            <button
              key={f.id || i}
              onClick={() => setPlaying(f)}
              data-testid={`film-${i}`}
              className="film-card group text-left reveal"
              style={{ transitionDelay: `${(i % 2) * 100}ms` }}
            >
              <div className="frame bg-[#1a1a1a] relative gsap-image-hover" style={{ aspectRatio: "16/9" }} data-gsap="image">
                {thumbnailUrl ? (
                  <img src={thumbnailUrl} alt={f.couple} loading="lazy" decoding="async" />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-[#1f1f1f] to-[#0d0d0d]" />
                )}
                <div className="absolute inset-0 bg-[rgba(0,0,0,0.25)] group-hover:bg-[rgba(0,0,0,0.45)] transition-colors flex items-center justify-center">
                  <div className="film-play-ring" style={{ width: "5rem", height: "5rem" }}>
                    <Play size={22} fill="currentColor"
                      className="ml-[3px] text-[#B8914A] group-hover:text-[#070707] transition-colors" />
                  </div>
                </div>
                {f.length && (
                  <span className="absolute bottom-3 right-3 eyebrow text-[0.58rem] bg-[rgba(0,0,0,0.65)] text-[#F5EFE4] px-2 py-1">
                    {f.length}
                  </span>
                )}
              </div>
              <div className="mt-5 flex items-end justify-between gap-4">
                <div>
                  <div className="font-display text-[clamp(1.6rem,2.5vw,2.2rem)] group-hover:text-[#B8914A] transition-colors">
                    {f.couple}
                  </div>
                  <div className="eyebrow text-[0.58rem] text-[rgba(184,145,74,0.75)] mt-2">{f.location}</div>
                </div>
                <span className="eyebrow text-[0.58rem] flex items-center gap-2 text-[rgba(245,239,228,0.6)] group-hover:text-[#B8914A] transition-colors flex-shrink-0">
                  Watch Film <ArrowRight size={11} />
                </span>
              </div>
            </button>
            );
          })}
        </div>
        )}
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 px-6 text-center border-t border-[rgba(184,145,74,0.14)]" data-gsap="reveal">
        <h2 className="font-display" style={{ fontSize: "clamp(2.2rem,4vw,3.8rem)" }}>
          Let's film your story.
        </h2>
        <p className="mt-3 text-[0.83rem] text-[rgba(245,239,228,0.55)]">Every wedding deserves a film as beautiful as the day itself.</p>
        <Link to="/contact" className="btn-gold-fill mt-10 inline-flex" data-testid="films-cta">
          Enquire Now <ArrowRight size={13} />
        </Link>
      </section>

      {/* Dialog */}
      {playing && (
        <Dialog open={!!playing} onOpenChange={() => setPlaying(null)}>
          <DialogContent className="max-w-4xl p-0 bg-[#070707] border-[rgba(184,145,74,0.2)]">
            <DialogTitle className="sr-only">
              {playing.couple || "Mangalfera Films"} film preview
            </DialogTitle>
            {playingEmbedUrl ? (
              <iframe
                src={playingEmbedUrl}
                title={`${playing.couple || "Mangalfera Films"} film`}
                className="w-full aspect-video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                allowFullScreen
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
              />
            ) : (
              <div className="aspect-video flex items-center justify-center text-[rgba(245,239,228,0.4)] eyebrow text-[0.65rem]">
                Film coming soon
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
